import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiPhone, FiMail, FiMapPin, FiClock, FiCheckCircle, FiGlobe } = FiIcons;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    urgency: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        industry: '',
        urgency: '',
        message: ''
      });
    }, 3000);
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-12 shadow-2xl border-2 border-emerald-500"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-green-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Thank You!
            </h3>
            <p className="text-gray-600 text-lg mb-6">
              Your compliance assessment request has been received. Our vMLRO expert will contact you within 24 hours.
            </p>
            <div className="bg-emerald-100 text-emerald-800 px-6 py-3 rounded-xl inline-block">
              <span className="font-semibold">Priority Response: High urgency cases contacted within 2 hours</span>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-100 rounded-full blur-3xl opacity-50"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Start Your Compliance Journey
            <br />
            <span className="text-emerald-700">Today</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Get a free compliance assessment and discover how our vMLRO services 
            can protect your business from regulatory risks.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3"
          >
            <div className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-3xl p-8 border-2 border-emerald-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Free Compliance Assessment
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Business Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      placeholder="Your Company Ltd"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Industry Sector *
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    >
                      <option value="">Select your industry</option>
                      <option value="fintech">FinTech & Payments</option>
                      <option value="realestate">Real Estate & Property</option>
                      <option value="professional">Professional Services</option>
                      <option value="gaming">Gaming & Entertainment</option>
                      <option value="crypto">Cryptocurrency & Digital Assets</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Compliance Urgency *
                  </label>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                  >
                    <option value="">Select urgency level</option>
                    <option value="immediate">Immediate (Regulatory deadline within 30 days)</option>
                    <option value="urgent">Urgent (Regulatory deadline within 90 days)</option>
                    <option value="planning">Planning (Preparing for future requirements)</option>
                    <option value="assessment">Assessment (Understanding current obligations)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Compliance Challenge Details
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Describe your specific AML/CFT compliance requirements or challenges..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-green-700 text-white px-8 py-5 rounded-xl hover:from-emerald-700 hover:to-green-800 transition-all duration-300 font-semibold text-lg shadow-lg flex items-center justify-center space-x-3"
                >
                  <span>Request Free Assessment</span>
                  <SafeIcon icon={FiSend} className="w-5 h-5" />
                </button>

                <p className="text-sm text-gray-500 text-center">
                  * Response within 24 hours. Urgent cases contacted within 2 hours.
                </p>
              </form>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="bg-gradient-to-br from-emerald-600 to-green-700 rounded-3xl p-8 text-white">
              <h4 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Direct Contact
              </h4>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiGlobe} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">vmlro.com</div>
                    <div className="text-emerald-100 text-sm">TruSecure SRL</div>
                    <div className="text-emerald-100 text-sm">An Oxford Holding Company</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMail} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">contact@vmlro.com</div>
                    <div className="text-emerald-100 text-sm">Expert Response Team</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">Romania</div>
                    <div className="text-emerald-100 text-sm">Global Compliance Coverage</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-emerald-200 rounded-3xl p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-6 h-6 text-emerald-600" />
                <span>Response Guarantee</span>
              </h4>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Immediate Urgency</span>
                  <span className="font-bold text-emerald-700">2 Hours</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Standard Inquiries</span>
                  <span className="font-bold text-emerald-700">24 Hours</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Assessment Delivery</span>
                  <span className="font-bold text-emerald-700">48 Hours</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-emerald-50 border-2 border-emerald-200 rounded-3xl p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-6">Trusted & Certified</h4>
              
              <div className="space-y-4">
                {[
                  'Certified AML/CFT Professionals',
                  'Multi-jurisdiction Expertise',
                  'Confidentiality Guaranteed',
                  'Professional Indemnity Insured'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;